import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { ModalService } from '@freelancer/ui';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { CardSize } from '@freelancer/ui/card';
import { Margin } from '@freelancer/ui/margin';
import { ModalColor, ModalSize } from '@freelancer/ui/modal';
import { SplitButtonOption } from '@freelancer/ui/split-button';
import {
  FontColor,
  FontStyle,
  FontType,
  FontWeight,
  TextSize,
} from '@freelancer/ui/text';
import { TimeUnitApi } from 'api-typings/common/common';
import { ContextTypeApi } from 'api-typings/messages/messages_types';
import { HireMeUser } from 'app/hire-me/hire-me.types';
import * as Rx from 'rxjs';
import { take } from 'rxjs/operators';
import { AwardProjectModel } from '../user-profile-award-project.types';

enum AwardMeSplitButtonOptions {
  AWARD_ME = 'Award',
  HIRE_ME = 'Hire Me',
  INVITE = 'Invite to Project',
}

@Component({
  selector: 'app-user-profile-award-project-sidebar',
  template: `
    <fl-card [size]="CardSize.SMALL">
      <fl-card-header-title i18n="Award freelancer card title">
        Award Freelancer
      </fl-card-header-title>

      <fl-text
        [flMarginBottom]="Margin.SMALL"
        i18n="Award freelancer header text"
      >
        <fl-text [fontType]="FontType.SPAN" [weight]="FontWeight.BOLD">
          {{ awardProjectModel.freelancerDisplayName }}
        </fl-text>
        <fl-text [fontType]="FontType.SPAN">
          placed a bid on your project
        </fl-text>
        <fl-link
          [link]="awardProjectModel.projectLink"
          flTrackingLabel="AwardProjectSidebarProjectLink"
        >
          {{ awardProjectModel.projectName }}
        </fl-link>
      </fl-text>

      <fl-text
        class="AwardProjectDesc"
        *ngIf="awardProjectModel.bidDesc"
        [color]="FontColor.MID"
        [flMarginBottom]="Margin.SMALL"
        [style]="FontStyle.ITALIC"
        [maxLines]="3"
      >
        "{{ awardProjectModel.bidDesc }}"
      </fl-text>

      <fl-bit
        *ngIf="(isDeloitteUserProfile$ | flAsync) === false"
        [flMarginBottom]="Margin.MID"
      >
        <fl-text
          [size]="TextSize.SMALL"
          [weight]="FontWeight.BOLD"
          [fontType]="FontType.CONTAINER"
        >
          {{
            awardProjectModel.bidAmount
              | flCurrency: awardProjectModel.currency.code
          }}

          <fl-text
            *ngIf="awardProjectModel.hourly; else fixedProject"
            i18n="Hourly project bid amount label"
            [fontType]="FontType.SPAN"
            [size]="TextSize.INHERIT"
            [weight]="FontWeight.BOLD"
          >
            /hr
          </fl-text>
          <ng-template #fixedProject>
            <fl-text
              i18n="Fixed project bid duration"
              [fontType]="FontType.SPAN"
              [size]="TextSize.INHERIT"
            >
              in {{ awardProjectModel.duration }} day(s)
            </fl-text>
          </ng-template>
        </fl-text>
        <ng-container *ngIf="awardProjectModel.hourly">
          <fl-text
            i18n="Hourly project bid duration"
            [weight]="FontWeight.BOLD"
            [fontType]="FontType.SPAN"
          >
            Hours:
          </fl-text>
          <fl-text [fontType]="FontType.SPAN">
            {{ awardProjectModel.duration }} /
            <ng-container [ngSwitch]="awardProjectModel?.commitment?.interval">
              <ng-container
                *ngSwitchCase="TimeUnitApi.SECOND"
                i18n="Second label"
              >
                second
              </ng-container>
              <ng-container
                *ngSwitchCase="TimeUnitApi.MINUTE"
                i18n="Minute label"
              >
                minute
              </ng-container>
              <ng-container *ngSwitchCase="TimeUnitApi.HOUR" i18n="Hour label">
                hour
              </ng-container>
              <ng-container *ngSwitchCase="TimeUnitApi.DAY" i18n="Day label">
                day
              </ng-container>
              <ng-container *ngSwitchCase="TimeUnitApi.WEEK" i18n="Week label">
                week
              </ng-container>
              <ng-container
                *ngSwitchCase="TimeUnitApi.MONTH"
                i18n="Month label"
              >
                month
              </ng-container>
              <ng-container *ngSwitchCase="TimeUnitApi.YEAR" i18n="Year label">
                year
              </ng-container>
              <ng-container
                *ngSwitchCase="TimeUnitApi.DECADE"
                i18n="Decade label"
              >
                decade
              </ng-container>
              <ng-container
                *ngSwitchCase="TimeUnitApi.CENTURY"
                i18n="Century label"
              >
                century
              </ng-container>
              <ng-container
                *ngSwitchCase="TimeUnitApi.MILLENIUM"
                i18n="Millenium label"
              >
                millenium
              </ng-container>
            </ng-container>
          </fl-text>
        </ng-container>
      </fl-bit>

      <fl-split-button
        i18n="Award award project sidebar button label"
        display="block"
        flTrackingLabel="AwardProjectSidebarAwardButton"
        [busy]="awardMeBusy"
        [color]="ButtonColor.SUCCESS"
        [flMarginBottom]="Margin.SMALL"
        [options]="awardMeButtonOptions"
        [size]="ButtonSize.LARGE"
        (primaryClicked)="handleAwardMeClicked()"
        (optionSelected)="onOptionSelected($event)"
      >
        Award
      </fl-split-button>
      <fl-chat-button
        flTrackingLabel="AwardProjectSidebarChatButton"
        display="block"
        [size]="ButtonSize.LARGE"
        [otherMemberIds]="[awardProjectModel.freelancerId]"
        [contextType]="ContextTypeApi.PROJECT"
        [contextId]="awardProjectModel.projectId"
      ></fl-chat-button>
    </fl-card>
  `,
  styleUrls: ['./user-profile-award-project-sidebar.component.scss'],
})
export class UserProfileAwardProjectSidebarComponent {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  CardSize = CardSize;
  FontColor = FontColor;
  TextSize = TextSize;
  FontStyle = FontStyle;
  FontType = FontType;
  FontWeight = FontWeight;
  Margin = Margin;
  ModalSize = ModalSize;
  TimeUnitApi = TimeUnitApi;
  ContextTypeApi = ContextTypeApi;

  @Input() awardProjectModel: AwardProjectModel;
  @Input() isDeloitteUserProfile$: Rx.Observable<boolean>;
  @Input() hireMeUser$: Rx.Observable<HireMeUser>;

  awardMeBusy = false;

  awardMeButtonOptions: readonly SplitButtonOption[] = [
    { copy: AwardMeSplitButtonOptions.AWARD_ME },
    { copy: AwardMeSplitButtonOptions.HIRE_ME },
    { copy: AwardMeSplitButtonOptions.INVITE },
  ];

  constructor(private modalService: ModalService, private router: Router) {}

  onOptionSelected(option: SplitButtonOption) {
    switch (option.copy) {
      case AwardMeSplitButtonOptions.HIRE_ME:
        this.handleHireMeSubOption();
        break;
      case AwardMeSplitButtonOptions.INVITE:
        this.handleInviteSubOption();
        break;
      default:
        break;
    }
  }

  handleAwardMeClicked(): void {
    this.awardMeBusy = true;

    this.router.navigate([this.awardProjectModel.projectLink], {
      fragment: 'proposals',
      queryParams: {
        awardBid: this.awardProjectModel.bidId,
      },
    });
  }

  handleHireMeSubOption(): void {
    this.hireMeUser$
      .pipe(take(1))
      .toPromise()
      .then(hireMeUser => {
        const userId = hireMeUser.profileUserId;
        this.modalService.open('HireMeWebappModal', {
          inputs: {
            profileUserId: userId,
          },
          color: ModalColor.DARK,
          size: ModalSize.XSMALL,
          edgeToEdge: true,
        });
      });
  }

  handleInviteSubOption(): void {
    this.hireMeUser$
      .pipe(take(1))
      .toPromise()
      .then(hireMeUser => {
        this.modalService.open('InviteModal', {
          size: ModalSize.SMALL,
          edgeToEdge: false,
          mobileFullscreen: true,
          inputs: {
            freelancerId: hireMeUser.profileUserId,
            freelancerName: hireMeUser.profileUserDisplayName,
          },
        });
      });
  }
}
